// SuperAdmin → Accounts tab: new companies, request aging by priority, AI cost per company.
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import EChart from '../charts/EChart';
import { ChartCard, EmptyState } from './shared';

const PRIORITY_ORDER = ['urgent', 'high', 'medium', 'low'];

const ageDays = (createdAt) => {
  if (!createdAt) return null;
  const d = new Date(createdAt).getTime();
  if (Number.isNaN(d)) return null;
  return Math.max(0, Math.floor((Date.now() - d) / (1000 * 60 * 60 * 24)));
};

const SuperAccounts = ({ view, t }) => {
  const { companiesByMonth = [], pendingRequests = [], aiByCompany = [] } = view;
  const safePendingRequests = Array.isArray(pendingRequests) ? pendingRequests : [];

  const newCompaniesOption = useMemo(() => {
    const safeRows = Array.isArray(companiesByMonth) ? companiesByMonth : [];
    if (!safeRows.length) return null;
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 40, right: 16, top: 24, bottom: 40 },
      xAxis: { type: 'category', data: safeRows.map((r) => r.label || r.month) },
      yAxis: { type: 'value', allowDecimals: false },
      series: [{ type: 'bar', data: safeRows.map((r) => r.count ?? r.value ?? 0), itemStyle: { borderRadius: [4, 4, 0, 0] } }]
    };
  }, [companiesByMonth]);

  const aging = useMemo(() => {
    const safeRequests = Array.isArray(pendingRequests) ? pendingRequests : [];
    if (!safeRequests.length) return null;
    const buckets = { urgent: [], high: [], medium: [], low: [], other: [] };
    safeRequests.forEach((r) => {
      const p = PRIORITY_ORDER.includes(r.priority) ? r.priority : 'other';
      buckets[p].push(r);
    });
    const avg = (rows) => {
      const safeRows = Array.isArray(rows) ? rows : [];
      const ages = safeRows.map((r) => ageDays(r.createdAt)).filter((a) => a != null);
      if (!ages.length) return 0;
      return Math.round(ages.reduce((s, a) => s + a, 0) / ages.length);
    };
    return PRIORITY_ORDER.map((p) => ({ priority: p, count: buckets[p].length, avgDays: avg(buckets[p]) }));
  }, [pendingRequests]);

  const agingOption = useMemo(() => {
    if (!aging || !aging.some((a) => a.count > 0)) return null;
    return {
      tooltip: { trigger: 'axis' },
      legend: { bottom: 0 },
      grid: { left: 40, right: 16, top: 24, bottom: 48 },
      xAxis: { type: 'category', data: aging.map((a) => a.priority) },
      yAxis: { type: 'value', allowDecimals: false },
      series: [
        { name: t('requestsCount'), type: 'bar', data: aging.map((a) => a.count), itemStyle: { borderRadius: [4, 4, 0, 0] } },
        { name: t('avgAgeDays'), type: 'line', smooth: true, data: aging.map((a) => a.avgDays) }
      ]
    };
  }, [aging, t]);

  const aiOption = useMemo(() => {
    const safeAi = Array.isArray(aiByCompany) ? aiByCompany : [];
    const rows = safeAi.slice(0, 15);
    if (!rows.length) return null;
    return {
      tooltip: { trigger: 'axis' },
      grid: { left: 8, right: 48, top: 16, bottom: 16, containLabel: true },
      xAxis: { type: 'value' },
      yAxis: { type: 'category', data: rows.map((r) => r.name || '—') },
      series: [{ type: 'bar', data: rows.map((r) => r.cost ?? r.value ?? 0), itemStyle: { borderRadius: [0, 4, 4, 0] } }]
    };
  }, [aiByCompany]);

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title={t('newCompaniesTitle')} subtitle={t('newCompaniesSubtitle')}>
          {newCompaniesOption ? <EChart option={newCompaniesOption} height={300} /> : <EmptyState message={t('noCompaniesTrend')} />}
        </ChartCard>
        <ChartCard title={t('agingTitle')} subtitle={t('agingSubtitle', { count: safePendingRequests.length })}>
          {agingOption ? <EChart option={agingOption} height={300} /> : <EmptyState message={t('noRequests')} />}
        </ChartCard>
      </div>
      <ChartCard title={t('aiTitle')} subtitle={t('aiSubtitle')}>
        {aiOption ? <EChart option={aiOption} height={320} /> : <EmptyState message={t('noAiData')} />}
      </ChartCard>
    </div>
  );
};

SuperAccounts.propTypes = {
  view: PropTypes.object.isRequired,
  t: PropTypes.func.isRequired
};

export default SuperAccounts;
